import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { useState } from 'react';
import { useController } from 'react-hook-form';

import { ExpandMoreOutlinedIcon } from '../../../../../components';

interface FormSearchSelectionOption {
  label: string;
  value: string;
}

interface FormSearchSelectionProps {
  name: string;
  options: FormSearchSelectionOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function FormSearchSelection(props: Readonly<FormSearchSelectionProps>) {
  const { name, options, placeholder, disabled, className } = props;

  const { t } = useTranslation();

  const {
    field,
    fieldState: { error },
  } = useController({ name });

  const [query, setQuery] = useState('');

  const selectedOption = options.find((option) => option.value === field.value) ?? null;

  const filteredOptions =
    query.trim() === ''
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className={cn('w-full', className)}>
      <Combobox
        as='div'
        className='relative w-full'
        immediate
        disabled={disabled}
        value={selectedOption}
        by='value'
        onChange={(option: FormSearchSelectionOption | null) => {
          field.onChange(option?.value ?? '');
          field.onBlur();
        }}
        onClose={() => setQuery('')}
      >
        <div
          className={cn(
            'focus-within:border-primary flex h-11 w-full items-center gap-2 rounded border border-neutral-300 bg-white px-3.5',
            disabled && 'cursor-not-allowed bg-gray-100',
            error && 'border-danger focus-within:border-danger',
          )}
        >
          <ComboboxInput
            ref={field.ref}
            aria-label={name}
            placeholder={placeholder}
            displayValue={(option: FormSearchSelectionOption | null) => option?.label ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            className='text-text-primary placeholder:text-text-disabled min-w-0 flex-1 bg-transparent text-base leading-7 outline-none'
          />
          <ComboboxButton className='shrink-0'>
            <ExpandMoreOutlinedIcon className='text-text-secondary' />
          </ComboboxButton>
        </div>

        <ComboboxOptions className='absolute top-[calc(100%+4px)] left-0 z-[1000] flex max-h-60 w-max max-w-[calc(100vw-2rem)] min-w-full flex-col gap-1 overflow-y-auto overscroll-contain rounded-lg border-[0.5px] border-neutral-200 bg-white p-2 shadow-[0px_4px_4px_rgba(52,58,64,0.1)] empty:hidden'>
          {filteredOptions.length === 0 ? (
            <div className='text-text-disabled px-2 py-1.5 text-base leading-6'>
              {t('questionnaire.searchSelection.noResults')}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption
                key={option.value}
                value={option}
                className='text-text-primary flex w-full cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-left text-base leading-6 whitespace-nowrap data-focus:bg-neutral-100 data-selected:bg-neutral-100 data-selected:font-medium'
              >
                {option.label}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>

      {error?.message ? (
        <p className='text-danger mt-2 text-start text-sm'>{t(error.message)}</p>
      ) : null}
    </div>
  );
}
